import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const nav = useNavigate();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
        if (password !== confirm) { toast.error("Passwords do not match"); return; }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            toast.success("Password reset! Please sign in.");
            nav("/login", { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Reset failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-white">
            <div className="hidden lg:flex flex-col justify-between p-12 border-r border-border grid-bg">
                <div>
                    <img src="/scout-ai-logo.png" alt="Scout AI" className="h-12 w-auto" />
                </div>
                <div className="max-w-md">
                    <p className="font-heading text-3xl font-bold leading-tight">
                        Choose a new password.
                    </p>
                    <p className="text-muted-foreground mt-3">
                        Make it strong — at least 6 characters. You'll use it to sign in next time.
                    </p>
                </div>
            </div>

            <div className="flex items-center justify-center p-8">
                <div className="w-full max-w-sm space-y-5">
                    <div>
                        <div className="overline text-muted-foreground">Reset password</div>
                        <h1 className="font-heading text-4xl font-black mt-2">Set a new password.</h1>
                    </div>

                    {!token ? (
                        <div className="card-os p-5" data-testid="reset-no-token">
                            <p className="text-sm text-foreground">
                                This reset link is invalid or incomplete.{" "}
                                <Link to="/forgot-password" className="text-brand font-medium hover:underline">Request a new one</Link>.
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-5">
                            <div>
                                <label className="overline text-muted-foreground">New password</label>
                                <input
                                    data-testid="reset-password"
                                    type="password" required value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="input-os mt-1" placeholder="••••••••"
                                />
                            </div>
                            <div>
                                <label className="overline text-muted-foreground">Confirm password</label>
                                <input
                                    data-testid="reset-confirm"
                                    type="password" required value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    className="input-os mt-1" placeholder="••••••••"
                                />
                            </div>
                            <button
                                data-testid="reset-submit"
                                type="submit" disabled={loading}
                                className="btn-primary w-full disabled:opacity-50"
                            >
                                {loading ? "Resetting…" : "Reset password"}
                            </button>
                            <div className="text-sm text-muted-foreground text-center">
                                <Link data-testid="back-to-login" to="/login" className="text-brand font-medium hover:underline">
                                    Back to sign in
                                </Link>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
